import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Layout } from "../../components/Layout";
import TabNavigation from "../../components/TabNavigation";
import { AppointmentCard } from "../../components/AppointmentCard";
import { Calendar, Frown, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import DashboardFilters from "./DashboardFilters";
import {
  getAppointmentsByTechIdAPI,
  updateAppointmentStatusAPI,
} from "./dashboardAPI";
import { clearAllTabs, setActiveTab } from "./dashboardSlice";
import clsx from "clsx";
import { useIntl, FormattedMessage } from "react-intl";
import { useToast } from "../../hooks/use-toast";

// Throttling function
function throttle(func, limit) {
  let inThrottle;
  return function (...args) {
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

function Dashboard({ onNavigate }) {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const intl = useIntl();
  const { toast } = useToast();
  const { tabs, activeTab } = useSelector((state) => state.dashboard);
  const { loading, error } = useSelector(
    (state) => state.dashboard.tabs[state.dashboard.activeTab]
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [updatingAppointments, setUpdatingAppointments] = useState(new Set());

  const getTechnicianId = () => {
    const user = localStorage.getItem("user");
    if (!user) return null;
    try {
      return JSON.parse(user)?.employeeid || null;
    } catch {
      return null;
    }
  };
  const technicianId = getTechnicianId();

  const currentTabData = tabs[activeTab] || {
    appointments: [],
    page: 1,
    hasMore: true,
    totalCount: 0,
  };
  const {
    appointments: currentAppointments,
    hasMore,
    totalCount,
  } = currentTabData;

  useEffect(() => {
    const tabData = tabs[activeTab];
    if (
      (technicianId && (!tabData || tabData.appointments.length === 0)) ||
      searchTerm ||
      startDate ||
      endDate
    ) {
      loadAppointments(true);
    }
  }, [activeTab, technicianId]);

  const loadAppointments = (reset = false, newFilters) => {
    if (!technicianId) return;

    const tabData = tabs[activeTab] || { page: 0, pageSize: 10 };
    const pageToFetch = reset ? 1 : tabData.page + 1;
    const filtersToUse =
      newFilters !== undefined
        ? newFilters
        : {
            searchQuery: searchTerm,
            startDate: startDate,
            endDate: endDate,
          };
    if (!reset) setLoadingMore(true);

    dispatch(
      getAppointmentsByTechIdAPI(
        technicianId,
        {
          pageNo: pageToFetch,
          pageSize: tabData.pageSize,
          searchQuery: filtersToUse.searchQuery,
          startDate: filtersToUse.startDate || null,
          endDate: filtersToUse.endDate || null,
          tab: activeTab,
        },
        !reset
      )
    ).finally(() => {
      setLoadingMore(false);
    });
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      !loadingMore &&
      hasMore
    ) {
      loadAppointments(false);
    }
  };

  useEffect(() => {
    const throttledScroll = throttle(handleScroll, 200);
    const scrollableElement = scrollRef.current;
    if (scrollableElement) {
      scrollableElement.addEventListener("scroll", throttledScroll);
    }
    return () => {
      if (scrollableElement) {
        scrollableElement.removeEventListener("scroll", throttledScroll);
      }
    };
  }, [hasMore, loadingMore, activeTab]);

  const handleTabChange = (newTab) => {
    dispatch(setActiveTab(newTab));
  };

  const handleRefresh = () => {
    loadAppointments(true);
  };

  const handleFilterSearch = () => {
    loadAppointments(true);
    setShowFilters(false);
  };

  const handleFilterClear = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setShowFilters(false);
    loadAppointments(true, { searchQuery: "", startDate: "", endDate: "" });
  };

  const handleStatusChange = (appointmentId, newStatus) => {
    setUpdatingAppointments((prev) => new Set(prev).add(appointmentId));
    dispatch(updateAppointmentStatusAPI(appointmentId, newStatus))
      .then(() => {
        dispatch(clearAllTabs());
        toast({
          title: "Success",
          description: "Appointment status has been updated.",
          variant: "success",
        });
        loadAppointments(true);
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.Message ||
          error?.response?.data ||
          error?.message ||
          "Failed to update status.";

        toast({
          title: "Update Failed",
          description: errorMessage,
          variant: "error",
        });
      })
      .finally(() => {
        setUpdatingAppointments((prev) => {
          const newSet = new Set(prev);
          newSet.delete(appointmentId);
          return newSet;
        });
      });
  };

  const filter = { query: searchTerm, startDate, endDate };
  const setFilter = (newFilter) => {
    setSearchTerm(newFilter.query || "");
    setStartDate(newFilter.startDate || "");
    setEndDate(newFilter.endDate || "");
  };

  const SkeletonLoader = ({ count = 5 }) => (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="p-4 border rounded-lg">
          <div className="flex justify-between items-start mb-3">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-6 w-20" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
          <div className="flex justify-between items-center mt-4">
            <Skeleton className="h-3 w-20" />
            <div className="flex space-x-2">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-16" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const tabsConfig = [
    { id: "active", label: <FormattedMessage id="Dashboard.Active" /> },
    { id: "scheduled", label: <FormattedMessage id="Dashboard.Scheduled" /> },
    { id: "closed", label: <FormattedMessage id="Dashboard.Closed" /> },
  ];

  return (
    <Layout
      title={<FormattedMessage id="Dashboard.Dashboard" />}
      onNavigate={onNavigate}
    >
      <div
        className={clsx("sticky top-0 z-20 bg-white dark:bg-gray-900")}
        style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}
      >
        <TabNavigation
          tabs={tabsConfig}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </div>
      <div className="flex justify-end gap-2 items-center">
        <Button
          onClick={handleRefresh}
          variant="ghost"
          size="sm"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
        <DashboardFilters
          show={showFilters}
          onToggle={() => setShowFilters(!showFilters)}
          filter={filter}
          setFilter={setFilter}
          onSearch={handleFilterSearch}
          onClear={handleFilterClear}
          loading={loading}
        />
      </div>
      <div
        ref={scrollRef}
        className="overflow-y-auto"
        style={{ maxHeight: "calc(100vh - 215px)", minHeight: 200 }}
      >
        {error ? (
          <div className="text-center text-red-600 py-10">
            <Frown className="mx-auto h-12 w-12 text-red-400" />
            <p className="text-lg font-medium mb-2">
              <FormattedMessage id="Dashboard.ErrorTitle" />
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {typeof error === "string" ? (
                error
              ) : (
                <FormattedMessage id="Dashboard.ErrorMessage" />
              )}
            </p>
            <Button onClick={handleRefresh} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />{" "}
              <FormattedMessage id="Dashboard.TryAgain" />
            </Button>
          </div>
        ) : loading && currentAppointments.length === 0 ? (
          <SkeletonLoader count={5} />
        ) : currentAppointments.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-lg font-medium mb-2">
              <FormattedMessage id="Dashboard.NoAppointments" />
            </p>
            <p className="text-sm">
              {searchTerm || startDate || endDate ? (
                <FormattedMessage id="Dashboard.TryAdjustingFilters" />
              ) : (
                <FormattedMessage id="Dashboard.NoAppointmentsTab" />
              )}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {currentAppointments.map((apt) => (
                <AppointmentCard
                  key={apt.id}
                  appointment={apt}
                  onViewDetails={() => onNavigate("appointment", apt.id)}
                  onStatusChange={(newStatus) =>
                    handleStatusChange(apt.id, newStatus)
                  }
                  isStatusUpdating={updatingAppointments.has(apt.id)}
                />
              ))}
            </div>
            {loadingMore && <SkeletonLoader count={3} />}
            {!hasMore && currentAppointments.length > 0 && (
              <div className="text-center py-4 text-gray-500 text-sm">
                <FormattedMessage
                  id="Dashboard.NoMoreAppointments"
                  values={{ total: totalCount }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default Dashboard;
